import { differenceInCalendarDays, endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export type SharedWorkInvoiceStatus = 'draft' | 'sent' | 'chasing' | 'paid';

export type SharedWorkIncomeEntry = {
  amount: number;
  receivedDate: string;
};

export type SharedWorkInvoiceEntry = {
  id: string;
  invoiceNumber: string;
  clientName: string;
  issueDate?: string | null;
  dueDate: string;
  amount: number;
  status: SharedWorkInvoiceStatus;
  notes?: string | null;
};

export type WorkIncomeMonth = {
  monthKey: string;
  label: string;
  totalIncome: number;
};

export type WorkCashflowAlert = {
  level: 'watch' | 'high';
  title: string;
  summary: string;
};

export type WorkChaseReminder = {
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  dueDate: string;
  daysUntilDue: number;
  status: SharedWorkInvoiceStatus;
  action: 'send' | 'prepare' | 'chase';
  title: string;
  body: string;
  tone: 'info' | 'warning' | 'error';
  priority: 'high' | 'medium' | 'low';
};

export type WorkCashflowSnapshot = {
  months: WorkIncomeMonth[];
  typicalMonthlyIncome: number | null;
  lowMonthFloor: number | null;
  lastCompleteMonth: WorkIncomeMonth | null;
  currentMonth: WorkIncomeMonth | null;
  currentMonthExpectedIncome: number | null;
  currentMonthOutstanding: number;
  monthEndGap: number | null;
  lowMonthDetected: boolean;
  currentMonthWatch: boolean;
  lowMonthAlert: WorkCashflowAlert | null;
  currentMonthAlert: WorkCashflowAlert | null;
  chaseReminders: WorkChaseReminder[];
};

export type WorkRightsPackId =
  | 'dismissal-redundancy'
  | 'pay-hours'
  | 'family-leave'
  | 'discrimination-advice';

export type WorkRightsPackMeta = {
  id: WorkRightsPackId;
  category: string;
  icon: string;
  eyebrow: string;
  title: string;
  summary: string;
  purpose: string;
};

export const WORK_RIGHTS_PACKS: WorkRightsPackMeta[] = [
  {
    id: 'dismissal-redundancy',
    category: 'Dismissal & Redundancy',
    icon: '⚖️',
    eyebrow: 'Rights pack',
    title: 'Dismissal, redundancy, and formal process',
    summary: 'Keep the formal route clear when work starts to feel shaky.',
    purpose: 'Use this when dismissal, redundancy, disciplinary, capability, or settlement language starts appearing.',
  },
  {
    id: 'pay-hours',
    category: 'Pay & Hours',
    icon: '💸',
    eyebrow: 'Rights pack',
    title: 'Pay, deductions, and working time',
    summary: 'Use this when money, hours, or missing pay are the pressure point.',
    purpose: 'Use this when unpaid wages, deductions, notice pay, holiday pay, hours, or breaks are the issue.',
  },
  {
    id: 'family-leave',
    category: 'Family & Leave',
    icon: '🫶',
    eyebrow: 'Rights pack',
    title: 'Family leave and flexible working',
    summary: 'Keep caring pressure visible before it becomes a work problem you keep re-explaining.',
    purpose: 'Use this when leave, caring, flexibility, or return-to-work needs should be put clearly in writing.',
  },
  {
    id: 'discrimination-advice',
    category: 'Discrimination & Rights at Work',
    icon: '📢',
    eyebrow: 'Rights pack',
    title: 'Discrimination, whistleblowing, and advice',
    summary: 'Use this when the issue is bigger than one awkward meeting and you need the right route.',
    purpose: 'Use this when discrimination, whistleblowing, or outside advice needs to be part of the next step.',
  },
];

export function getWorkRightsPackByCategory(category: string) {
  return WORK_RIGHTS_PACKS.find((pack) => pack.category === category) || null;
}

function roundCurrency(value: number) {
  return Math.round(value * 100) / 100;
}

function monthKey(value: Date) {
  return format(value, 'yyyy-MM');
}

function safeParseIso(value: string) {
  const parsed = parseISO(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function buildWorkInvoiceFileName(
  invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber'>,
  extension: 'pdf' | 'html' | 'txt' = 'pdf',
) {
  const safeName = invoice.invoiceNumber.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'invoice';
  return `${safeName}.${extension}`;
}

export function buildWorkInvoiceHtml(invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'issueDate' | 'dueDate' | 'amount' | 'notes'>) {
  const issueDate = format(parseISO(invoice.issueDate || invoice.dueDate), 'd MMM yyyy');
  const dueDate = format(parseISO(invoice.dueDate), 'd MMM yyyy');
  const notes = invoice.notes?.trim() || '';

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>${escapeHtml(invoice.invoiceNumber)}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 40px; color: #1f2937; }
      .header { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 32px; }
      .label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; margin-bottom: 6px; }
      .value { font-size: 16px; font-weight: 600; }
      .amount { font-size: 28px; font-weight: 700; margin-top: 8px; }
      .panel { border: 1px solid #d1d5db; border-radius: 14px; padding: 16px; margin-top: 16px; }
      pre { white-space: pre-wrap; font: inherit; margin: 0; color: #4b5563; }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <div class="label">Invoice</div>
        <div class="value">${escapeHtml(invoice.invoiceNumber)}</div>
      </div>
      <div>
        <div class="label">Client</div>
        <div class="value">${escapeHtml(invoice.clientName)}</div>
      </div>
    </div>
    <div class="panel">
      <div class="label">Amount due</div>
      <div class="amount">£${invoice.amount.toFixed(2)}</div>
    </div>
    <div class="panel">
      <div class="label">Issue date</div>
      <div class="value">${escapeHtml(issueDate)}</div>
    </div>
    <div class="panel">
      <div class="label">Due date</div>
      <div class="value">${escapeHtml(dueDate)}</div>
    </div>
    ${notes ? `<div class="panel"><div class="label">Notes</div><pre>${escapeHtml(notes)}</pre></div>` : ''}
  </body>
</html>`;
}

function wrapPdfText(text: string, maxWidth: number, font: any, size: number) {
  const paragraphs = text.split('\n');
  const lines: string[] = [];

  for (const paragraph of paragraphs) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      continue;
    }

    let currentLine = '';
    for (const word of words) {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        currentLine = candidate;
        continue;
      }

      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines;
}

function drawPdfParagraph({
  text,
  x,
  y,
  maxWidth,
  font,
  size,
  lineHeight,
  color,
  page,
}: {
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  font: any;
  size: number;
  lineHeight: number;
  color: ReturnType<typeof rgb>;
  page: any;
}) {
  const lines = wrapPdfText(text, maxWidth, font, size);
  let cursorY = y;

  lines.forEach((line) => {
    if (line) {
      page.drawText(line, {
        x,
        y: cursorY,
        size,
        font,
        color,
      });
    }
    cursorY -= lineHeight;
  });

  return cursorY;
}

async function createWorkInvoicePdfDocument(
  invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'issueDate' | 'dueDate' | 'amount' | 'notes'>,
) {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.setCreator('Adulting');
  pdfDoc.setProducer('Adulting');
  pdfDoc.setTitle(`Invoice ${invoice.invoiceNumber}`);

  const page = pdfDoc.addPage([595.28, 841.89]);
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pageWidth = page.getWidth();
  const pageHeight = page.getHeight();
  const margin = 52;
  const contentWidth = pageWidth - margin * 2;
  const muted = rgb(0.42, 0.45, 0.49);
  const primary = rgb(0.17, 0.13, 0.2);
  const accent = rgb(0.36, 0.42, 0.28);
  const border = rgb(0.87, 0.87, 0.89);

  let cursorY = pageHeight - margin;

  page.drawText('Adulting invoice', {
    x: margin,
    y: cursorY,
    size: 11,
    font: regularFont,
    color: accent,
  });
  cursorY -= 30;

  page.drawText(invoice.invoiceNumber, {
    x: margin,
    y: cursorY,
    size: 24,
    font: boldFont,
    color: primary,
  });
  cursorY -= 22;

  page.drawText(invoice.clientName, {
    x: margin,
    y: cursorY,
    size: 13,
    font: regularFont,
    color: muted,
  });
  cursorY -= 34;

  page.drawRectangle({
    x: margin,
    y: cursorY - 78,
    width: contentWidth,
    height: 78,
    borderColor: border,
    borderWidth: 1,
  });

  page.drawText('Amount due', {
    x: margin + 18,
    y: cursorY - 24,
    size: 10,
    font: regularFont,
    color: muted,
  });
  page.drawText(`£${invoice.amount.toFixed(2)}`, {
    x: margin + 18,
    y: cursorY - 56,
    size: 28,
    font: boldFont,
    color: primary,
  });
  cursorY -= 110;

  const fieldWidth = (contentWidth - 14) / 2;
  const issueDate = format(parseISO(invoice.issueDate || invoice.dueDate), 'd MMM yyyy');
  const dueDate = format(parseISO(invoice.dueDate), 'd MMM yyyy');
  [
    { label: 'Issue date', value: issueDate, x: margin },
    { label: 'Due date', value: dueDate, x: margin + fieldWidth + 14 },
  ].forEach((field) => {
    page.drawRectangle({
      x: field.x,
      y: cursorY - 62,
      width: fieldWidth,
      height: 62,
      borderColor: border,
      borderWidth: 1,
    });
    page.drawText(field.label, {
      x: field.x + 16,
      y: cursorY - 22,
      size: 10,
      font: regularFont,
      color: muted,
    });
    page.drawText(field.value, {
      x: field.x + 16,
      y: cursorY - 46,
      size: 14,
      font: boldFont,
      color: primary,
    });
  });
  cursorY -= 86;

  const notes = invoice.notes?.trim();
  if (notes) {
    const noteLines = wrapPdfText(notes, contentWidth - 32, regularFont, 12);
    const noteHeight = Math.max(72, noteLines.length * 18 + 30);
    page.drawRectangle({
      x: margin,
      y: cursorY - noteHeight,
      width: contentWidth,
      height: noteHeight,
      borderColor: border,
      borderWidth: 1,
    });
    page.drawText('Notes', {
      x: margin + 16,
      y: cursorY - 22,
      size: 10,
      font: regularFont,
      color: muted,
    });
    drawPdfParagraph({
      text: notes,
      x: margin + 16,
      y: cursorY - 46,
      maxWidth: contentWidth - 32,
      font: regularFont,
      size: 12,
      lineHeight: 18,
      color: primary,
      page,
    });
  }

  return pdfDoc;
}

export async function buildWorkInvoicePdfBytes(
  invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'issueDate' | 'dueDate' | 'amount' | 'notes'>,
) {
  const pdfDoc = await createWorkInvoicePdfDocument(invoice);
  return pdfDoc.save();
}

export async function buildWorkInvoicePdfBase64(
  invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'issueDate' | 'dueDate' | 'amount' | 'notes'>,
) {
  const pdfDoc = await createWorkInvoicePdfDocument(invoice);
  return pdfDoc.saveAsBase64();
}

export function buildWorkInvoiceSendDraft(invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'issueDate' | 'dueDate' | 'amount' | 'notes'>) {
  const issueDate = format(parseISO(invoice.issueDate || invoice.dueDate), 'd MMM yyyy');
  const dueDate = format(parseISO(invoice.dueDate), 'd MMM yyyy');
  const notes = invoice.notes?.trim() || '';

  return `Hi ${invoice.clientName},

Please find invoice ${invoice.invoiceNumber} attached for £${invoice.amount.toFixed(2)}.

Issue date: ${issueDate}
Due date: ${dueDate}
${notes ? `\nNotes:\n${notes}\n` : ''}
If you need anything adjusted before payment, let me know and I can sort it quickly.

Thanks,`;
}

export function buildWorkInvoiceEmailSubject(
  invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName'>,
) {
  return `Invoice ${invoice.invoiceNumber} for ${invoice.clientName}`;
}

export function buildWorkInvoiceMailtoUrl(
  invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'issueDate' | 'dueDate' | 'amount' | 'notes'>,
) {
  const subject = encodeURIComponent(buildWorkInvoiceEmailSubject(invoice));
  const body = encodeURIComponent(buildWorkInvoiceSendDraft(invoice));
  return `mailto:?subject=${subject}&body=${body}`;
}

export function buildWorkInvoiceChaseDraft(invoice: Pick<SharedWorkInvoiceEntry, 'invoiceNumber' | 'clientName' | 'dueDate' | 'amount'>) {
  return `Hi ${invoice.clientName},

I am following up on invoice ${invoice.invoiceNumber} for £${invoice.amount.toFixed(2)}, which was due on ${format(parseISO(invoice.dueDate), 'd MMM yyyy')}.

Please can you confirm when payment will be made. If there is anything missing from my side, let me know and I will sort it quickly.

Thanks,`;
}

function median(values: number[]) {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return roundCurrency((sorted[middle - 1] + sorted[middle]) / 2);
  }
  return roundCurrency(sorted[middle]);
}

export function getWorkInvoiceReminder(
  invoice: SharedWorkInvoiceEntry,
  today: Date = new Date(),
): WorkChaseReminder | null {
  if (invoice.status === 'paid') return null;

  const dueDate = safeParseIso(invoice.dueDate);
  if (!dueDate) return null;

  const daysUntilDue = differenceInCalendarDays(dueDate, today);

  if (invoice.status === 'draft') {
    return {
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      clientName: invoice.clientName,
      amount: invoice.amount,
      dueDate: invoice.dueDate,
      daysUntilDue,
      status: invoice.status,
      action: 'send',
      title: 'Ready to send',
      body: 'Send the invoice now so this does not slip from draft into a cashflow problem.',
      tone: 'info',
      priority: daysUntilDue <= 3 ? 'high' : 'medium',
    };
  }

  if (daysUntilDue < 0) {
    return {
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      clientName: invoice.clientName,
      amount: invoice.amount,
      dueDate: invoice.dueDate,
      daysUntilDue,
      status: invoice.status,
      action: 'chase',
      title: 'Chase now',
      body: `${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) === 1 ? '' : 's'} overdue. Chase this today so payment does not drift further.`,
      tone: 'error',
      priority: 'high',
    };
  }

  if (daysUntilDue <= 3) {
    return {
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      clientName: invoice.clientName,
      amount: invoice.amount,
      dueDate: invoice.dueDate,
      daysUntilDue,
      status: invoice.status,
      action: 'prepare',
      title: 'Due soon',
      body:
        daysUntilDue === 0
          ? 'Due today. Keep the chase wording and invoice file ready in case payment does not land.'
          : `Due in ${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'}. Keep the chase wording ready before this tips overdue.`,
      tone: 'warning',
      priority: 'medium',
    };
  }

  return null;
}

export function buildWorkInvoiceReminderQueue(
  invoices: SharedWorkInvoiceEntry[],
  today: Date = new Date(),
) {
  const priorityOrder: Record<WorkChaseReminder['priority'], number> = {
    high: 0,
    medium: 1,
    low: 2,
  };

  return invoices
    .map((invoice) => getWorkInvoiceReminder(invoice, today))
    .filter((entry): entry is WorkChaseReminder => Boolean(entry))
    .sort((left, right) => {
      const priorityGap = priorityOrder[left.priority] - priorityOrder[right.priority];
      if (priorityGap !== 0) return priorityGap;
      if (left.daysUntilDue !== right.daysUntilDue) return left.daysUntilDue - right.daysUntilDue;
      return left.clientName.localeCompare(right.clientName);
    });
}

export function buildWorkCashflowSnapshot({
  incomeEntries,
  invoices,
  monthlyRunningCost,
  today = new Date(),
}: {
  incomeEntries: SharedWorkIncomeEntry[];
  invoices: SharedWorkInvoiceEntry[];
  monthlyRunningCost: number;
  today?: Date;
}): WorkCashflowSnapshot {
  const currentMonthDate = startOfMonth(today);
  const monthSeeds = Array.from({ length: 6 }, (_, index) => {
    const date = subMonths(currentMonthDate, 5 - index);
    return {
      monthKey: monthKey(date),
      label: format(date, 'MMM'),
      totalIncome: 0,
    } satisfies WorkIncomeMonth;
  });

  const monthsByKey = new Map(monthSeeds.map((month) => [month.monthKey, { ...month }]));

  for (const entry of incomeEntries) {
    const entryDate = safeParseIso(entry.receivedDate);
    if (!entryDate) continue;
    const key = monthKey(entryDate);
    const month = monthsByKey.get(key);
    if (!month) continue;
    month.totalIncome = roundCurrency(month.totalIncome + Math.max(entry.amount || 0, 0));
  }

  const months = monthSeeds.map((seed) => monthsByKey.get(seed.monthKey) || seed);
  const completeMonths = months.slice(0, -1);
  const completeMonthsWithIncome = completeMonths.filter((month) => month.totalIncome > 0);
  const typicalMonthlyIncome = median(completeMonthsWithIncome.map((month) => month.totalIncome));
  const lowMonthFloor =
    typicalMonthlyIncome !== null
      ? roundCurrency(Math.max(monthlyRunningCost, typicalMonthlyIncome * 0.75))
      : monthlyRunningCost > 0
        ? roundCurrency(monthlyRunningCost)
        : null;

  const lastCompleteMonth = completeMonths[completeMonths.length - 1] || null;
  const currentMonth = months[months.length - 1] || null;
  const currentMonthEnd = endOfMonth(today);
  const currentMonthOutstanding = roundCurrency(
    invoices.reduce((sum, invoice) => {
      if (invoice.status === 'paid' || invoice.status === 'draft') return sum;
      const dueDate = safeParseIso(invoice.dueDate);
      if (!dueDate || dueDate > currentMonthEnd) return sum;
      return sum + Math.max(invoice.amount || 0, 0);
    }, 0),
  );
  const currentMonthExpectedIncome =
    currentMonth && lowMonthFloor !== null ? roundCurrency(currentMonth.totalIncome + currentMonthOutstanding) : null;
  const monthEndGap =
    currentMonthExpectedIncome !== null && lowMonthFloor !== null
      ? roundCurrency(currentMonthExpectedIncome - lowMonthFloor)
      : null;

  const hasEnoughHistory = completeMonthsWithIncome.length >= 2;
  const lowMonthDetected = Boolean(
    hasEnoughHistory
      && lowMonthFloor !== null
      && lastCompleteMonth
      && lastCompleteMonth.totalIncome + 0.01 < lowMonthFloor,
  );
  const currentMonthWatch = Boolean(
    hasEnoughHistory
      && lowMonthFloor !== null
      && currentMonthExpectedIncome !== null
      && today.getDate() >= 20
      && currentMonthExpectedIncome + 0.01 < lowMonthFloor,
  );

  const lowMonthAlert = lowMonthDetected && lowMonthFloor !== null && lastCompleteMonth
    ? {
        level: lastCompleteMonth.totalIncome < monthlyRunningCost ? 'high' : 'watch',
        title: 'Low month detected',
        summary:
          `${lastCompleteMonth.label} brought in £${lastCompleteMonth.totalIncome.toFixed(0)} against a safer floor of £${lowMonthFloor.toFixed(0)}. ` +
          'Open Survival and plan around the leaner month, not just the better one.',
      } satisfies WorkCashflowAlert
    : null;

  const currentMonthAlert = currentMonthWatch && lowMonthFloor !== null && currentMonthExpectedIncome !== null
    ? {
        level: currentMonthExpectedIncome < monthlyRunningCost ? 'high' : 'watch',
        title: 'This month is tracking light',
        summary:
          `Even after invoices due by month-end, this month is tracking at about £${currentMonthExpectedIncome.toFixed(0)} against the same £${lowMonthFloor.toFixed(0)} floor. ` +
          'Use Survival now if essentials would get squeezed.',
      } satisfies WorkCashflowAlert
    : null;

  return {
    months,
    typicalMonthlyIncome,
    lowMonthFloor,
    lastCompleteMonth,
    currentMonth,
    currentMonthExpectedIncome,
    currentMonthOutstanding,
    monthEndGap,
    lowMonthDetected,
    currentMonthWatch,
    lowMonthAlert,
    currentMonthAlert,
    chaseReminders: buildWorkInvoiceReminderQueue(invoices, today),
  };
}
